import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Create a context for theme management
export const ThemeContext = createContext();

// Theme colors for easy access in components
const themeColors = {
  brandPrimary: '--moto-brand-primary',
  brandSecondary: '--moto-brand-secondary',
  brandAccent: '--moto-brand-accent',
  primary: '--bs-primary',
  secondary: '--bs-secondary',
  success: '--bs-success',
  info: '--bs-info',
  warning: '--bs-warning',
  danger: '--bs-danger',
  light: '--bs-light',
  dark: '--bs-dark',
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Check if user has a theme preference stored
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) return savedTheme;
    
    // Check if user prefers dark mode at the OS level
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? 'dark' 
      : 'light';
  };

  const [theme, setTheme] = useState(getInitialTheme);

  // Listen for auth changes to load user preferences
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Try to get user preferences from Firestore
          const userPrefsRef = doc(db, "userPreferences", user.uid);
          const userPrefsSnap = await getDoc(userPrefsRef);
          
          if (userPrefsSnap.exists()) {
            const prefs = userPrefsSnap.data();
            if (prefs.theme) {
              setTheme(prefs.theme);
              localStorage.setItem('theme', prefs.theme);
            }
          }
        } catch (error) {
          console.error('Error loading user theme preference:', error);
          // Continue with local storage theme on error
        }
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light';
      return newTheme;
    });
  };
  
  // Get a CSS variable value
  const getThemeColor = (colorVar) => {
    return getComputedStyle(document.documentElement).getPropertyValue(colorVar);
  };
  
  // Apply theme changes to document
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Create a theme colors object that provides access to all colors
  const colors = {};
  Object.entries(themeColors).forEach(([key, value]) => {
    colors[key] = getThemeColor(value);
  });
  
  return (
    <ThemeContext.Provider value={{ 
      theme, 
      toggleTheme,
      colors,
      getThemeColor
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme context
export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;